import { type SxProps, type Theme, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { isNullish } from 'remeda';
import { GenericForm, Loader } from 'src/components';
import {
  useGroupRules,
  usePartnerGroupRules,
  UserGroupRules,
} from 'src/graphql/entities/group-rules';
import { usePartner, usePartners } from 'src/graphql/entities/partner/partner';
import { usePartnerOnesheet } from 'src/graphql/entities/partner-onesheet';
import { CompanyInfoForm } from 'src/pages/Settings/company-info/company-info-form';
import { CompanyInfoContainer } from 'src/pages/Settings/company-info/styled';
import {
  CompanyInfoFormValues,
  CompanyInfoPartner,
  DEFAULT_FORM_VALUES,
  getFormValuesFromPartner,
  getStateCode,
} from 'src/pages/Settings/company-info/types';
import { useGlobalStore } from 'src/store';

const centeredSx: SxProps<Theme> = {
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '10rem',
};

export const CompanyDetailsTab = () => {
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const { rawPartnerMap, loading: partnersLoading } = usePartners();
  const { updatePartner, updatePartnerLoading } = usePartner();
  const { updatePartnerOnesheet } = usePartnerOnesheet(selectedPartnerId);
  const { loading: rulesLoading } = usePartnerGroupRules(selectedPartnerId);
  const { groupRules, loading: userRulesLoading } = useGroupRules();

  const partner = useMemo<CompanyInfoPartner | null>(() => {
    if (isNullish(selectedPartnerId)) return null;
    const raw = rawPartnerMap.get(selectedPartnerId);
    if (!raw) return null;

    return {
      partnerId: raw.partnerId,
      businessName: raw.businessName,
      zendeskDomain: raw.zendeskDomain,
      supportEmail: raw.supportEmail,
      supportPhone: raw.supportPhone,
      mainOfficePhone: raw.partnerOnesheet?.[0]?.mainOfficePhone ?? null,
      membershipEmail: raw.membershipEmail,
      membershipSalesCalendlyLink: raw.membershipSalesCalendlyLink,
      address: raw.address,
      city: raw.city,
      state: raw.state,
      zip: raw.zip,
      timeZone: raw.timeZone,
      billingEmail: raw.billingEmail,
      websiteUrl: raw.websiteUrl,
    };
  }, [selectedPartnerId, rawPartnerMap]);

  const isAdmin = useMemo(
    () => !!(groupRules[UserGroupRules.Admin] || groupRules[UserGroupRules.SuperAdmin]),
    [groupRules],
  );

  const loading = partnersLoading || rulesLoading || userRulesLoading;

  const formMethods = useForm<CompanyInfoFormValues>({
    defaultValues: DEFAULT_FORM_VALUES,
    mode: 'onChange',
  });

  useEffect(() => {
    if (partner) {
      formMethods.reset(getFormValuesFromPartner(partner));
    }
  }, [partner, formMethods]);

  const handleSave = useCallback(async () => {
    if (isNullish(selectedPartnerId)) return;

    const isFormValid = await formMethods.trigger();
    if (!isFormValid) return;

    const values = formMethods.getValues();
    const raw = rawPartnerMap.get(selectedPartnerId);
    const partnerOnesheet = raw?.partnerOnesheet?.[0];

    await Promise.all([
      updatePartner(selectedPartnerId, {
        address: values.address,
        city: values.city,
        state: getStateCode(values.state),
        zip: values.zip,
        timeZone: values.timeZone,
        billingEmail: values.billingEmail,
        websiteUrl: values.websiteUrl,
      }),
      partnerOnesheet
        ? updatePartnerOnesheet({ mainOfficePhone: values.mainOfficePhone }, partnerOnesheet)
        : Promise.resolve(),
    ]);

    formMethods.reset(values);
  }, [selectedPartnerId, formMethods, updatePartner, updatePartnerOnesheet, rawPartnerMap]);

  if (loading) {
    return (
      <CompanyInfoContainer sx={centeredSx}>
        <Loader />
      </CompanyInfoContainer>
    );
  }

  if (!partner) {
    return (
      <CompanyInfoContainer sx={centeredSx}>
        <Typography variant="body1" color="text.secondary">
          No partner selected
        </Typography>
      </CompanyInfoContainer>
    );
  }

  return (
    <CompanyInfoContainer>
      <GenericForm<CompanyInfoFormValues> formMethods={formMethods} gap={3}>
        <CompanyInfoForm
          isAdmin={isAdmin}
          isUpdating={updatePartnerLoading}
          onSave={handleSave}
        />
      </GenericForm>
    </CompanyInfoContainer>
  );
};
